import React, { useEffect, useState } from "react";
import AppLayout from "@/components/AppLayout";
import { api, API } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { FileText, Trash2, Link as LinkIcon, Copy, IndianRupee } from "lucide-react";
import { toast } from "sonner";
import { formatDistanceToNow } from "date-fns";

const STATUS_COLOR = {
  draft:     { bg: "#F1F5F9", fg: "#475569" },
  sent:      { bg: "#DBEAFE", fg: "#1D4ED8" },
  partial:   { bg: "#FEF3C7", fg: "#B45309" },
  paid:      { bg: "#DCFCE7", fg: "#15803D" },
  overdue:   { bg: "#FEE2E2", fg: "#B91C1C" },
  cancelled: { bg: "#E2E8F0", fg: "#64748B" },
};
const STATUSES = ["draft", "sent", "partial", "paid", "overdue", "cancelled"];

async function downloadPdf(id, number) {
  const token = sessionStorage.getItem("miro_token");
  const res = await fetch(`${API}/invoices/${id}/pdf`, { headers: { Authorization: `Bearer ${token}` }});
  if (!res.ok) return toast.error("Download failed");
  const blob = await res.blob();
  const link = document.createElement("a");
  link.href = URL.createObjectURL(blob);
  link.download = `${number}.pdf`;
  document.body.appendChild(link);
  link.click();
  link.remove();
}

export default function Invoices() {
  const [items, setItems] = useState([]);

  const load = async () => setItems((await api.get("/invoices")).data);
  useEffect(() => { load(); }, []);

  const remove = async (id) => {
    if (!window.confirm("Delete invoice?")) return;
    await api.delete(`/invoices/${id}`);
    load();
  };

  const genLink = async (id) => {
    try {
      const r = await api.post(`/invoices/${id}/payment-link`);
      await navigator.clipboard.writeText(r.data.payment_link);
      toast.success("Razorpay payment link copied to clipboard", { description: r.data.payment_link });
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Link generation failed");
    }
  };

  const checkout = async (id) => {
    try {
      const r = await api.post(`/invoices/${id}/create-order`);
      const { order_id, amount, currency, key_id, invoice_number, client_name, client_email, client_phone } = r.data;
      if (!window.Razorpay) return toast.error("Razorpay checkout not loaded yet");

      const options = {
        key: key_id,
        amount,
        currency,
        order_id,
        name: "MIRO Consultancy",
        description: `Invoice ${invoice_number}`,
        image: "/miro-logo.png",
        theme: { color: "#041B46" },
        prefill: {
          name: client_name || "",
          email: client_email || "",
          contact: client_phone || "",
        },
        modal: {
          ondismiss: () => toast.message("Checkout cancelled"),
        },
        handler: async (resp) => {
          try {
            const v = await api.post("/invoices/verify-payment", {
              invoice_id: id,
              razorpay_order_id: resp.razorpay_order_id,
              razorpay_payment_id: resp.razorpay_payment_id,
              razorpay_signature: resp.razorpay_signature,
            });
            toast.success("Payment verified — invoice marked paid", { description: `Payment ID: ${v.data.payment_id}` });
            load();
          } catch (err) {
            toast.error(err?.response?.data?.detail || "Signature verification failed");
          }
        },
      };
      const rzp = new window.Razorpay(options);
      rzp.on("payment.failed", (e) => {
        toast.error(e?.error?.description || "Payment failed", {
          description: e?.error?.reason || "Please retry",
        });
      });
      rzp.open();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Unable to start checkout");
    }
  };

  const updateStatus = async (id, status) => {
    await api.patch(`/invoices/${id}`, { status });
    load();
  };

  const totals = items.reduce((acc, i) => {
    acc.total += i.total || 0;
    if (i.status === "paid") acc.paid += i.total || 0;
    if (["draft","sent","partial","overdue"].includes(i.status)) acc.outstanding += (i.total - (i.amount_paid || 0));
    return acc;
  }, { total: 0, paid: 0, outstanding: 0 });

  return (
    <AppLayout title="Invoices">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-5">
        <StatCard label="Total invoiced" value={`₹${Math.round(totals.total).toLocaleString("en-IN")}`} accent="#041B46"/>
        <StatCard label="Paid" value={`₹${Math.round(totals.paid).toLocaleString("en-IN")}`} accent="#5FBF3A"/>
        <StatCard label="Outstanding" value={`₹${Math.round(totals.outstanding).toLocaleString("en-IN")}`} accent="#F59E0B"/>
      </div>

      <div className="bg-white border border-[var(--miro-border)] rounded-xl overflow-x-auto">
        <table data-testid="invoices-table" className="w-full text-sm">
          <thead className="bg-[var(--miro-bg)] text-[var(--miro-text-secondary)]">
            <tr className="text-left">
              <th className="px-4 py-3 font-semibold">Number</th>
              <th className="px-4 py-3 font-semibold">Client</th>
              <th className="px-4 py-3 font-semibold text-right">Total</th>
              <th className="px-4 py-3 font-semibold">Status</th>
              <th className="px-4 py-3 font-semibold">Payment link</th>
              <th className="px-4 py-3 font-semibold">Issued</th>
              <th className="px-4 py-3 text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {items.length === 0 && (
              <tr><td colSpan={7} className="px-4 py-12 text-center text-[var(--miro-text-muted)]">
                No invoices yet. Create a quotation and click <b>Convert</b> to make one.
              </td></tr>
            )}
            {items.map(i => (
              <tr key={i.id} data-testid={`inv-row-${i.id}`} className="border-t border-[var(--miro-border)]">
                <td className="px-4 py-3 font-mono font-semibold text-[var(--miro-navy)]">{i.number}</td>
                <td className="px-4 py-3">
                  <div className="font-semibold text-[var(--miro-navy)]">{i.client_name}</div>
                  <div className="text-xs text-[var(--miro-text-muted)]">{i.title}</div>
                </td>
                <td className="px-4 py-3 text-right font-mono font-semibold text-[var(--miro-navy)]">₹{i.total.toLocaleString("en-IN")}</td>
                <td className="px-4 py-3">
                  <Select value={i.status} onValueChange={(v) => updateStatus(i.id, v)}>
                    <SelectTrigger data-testid={`inv-status-${i.id}`} className="h-8 w-28 text-xs"
                                   style={{ background: STATUS_COLOR[i.status]?.bg, color: STATUS_COLOR[i.status]?.fg, border: "none" }}>
                      <SelectValue/>
                    </SelectTrigger>
                    <SelectContent>{STATUSES.map(s => <SelectItem key={s} value={s} className="capitalize">{s}</SelectItem>)}</SelectContent>
                  </Select>
                </td>
                <td className="px-4 py-3 text-xs">
                  {i.payment_link ? (
                    <button
                      data-testid={`inv-link-${i.id}`}
                      onClick={() => { navigator.clipboard.writeText(i.payment_link); toast.success("Link copied"); }}
                      className="flex items-center gap-1 text-[var(--miro-blue-600)] hover:underline"
                      title={i.payment_link}
                    >
                      <LinkIcon size={12}/> {i.payment_link.slice(0, 26)}…<Copy size={11}/>
                    </button>
                  ) : (
                    <span className="text-[var(--miro-text-muted)]">—</span>
                  )}
                </td>
                <td className="px-4 py-3 text-xs text-[var(--miro-text-muted)]">{formatDistanceToNow(new Date(i.created_at), { addSuffix: true })}</td>
                <td className="px-4 py-3 text-right space-x-1">
                  <Button data-testid={`inv-pdf-${i.id}`} size="sm" variant="outline"
                          onClick={() => downloadPdf(i.id, i.number)} className="h-8">
                    <FileText size={14} className="mr-1"/>PDF
                  </Button>
                  <Button data-testid={`inv-genlink-${i.id}`} size="sm"
                          onClick={() => genLink(i.id)} className="h-8"
                          style={{ background: "var(--miro-blue)" }}>
                    <LinkIcon size={14} className="mr-1"/>Pay link
                  </Button>
                  <button data-testid={`inv-delete-${i.id}`} onClick={() => remove(i.id)}
                          className="text-[var(--miro-text-muted)] hover:text-red-600 p-1.5 inline-flex">
                    <Trash2 size={14}/>
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="mt-4 text-xs text-[var(--miro-text-muted)]">
        <span className="font-semibold text-[var(--miro-navy)]">Razorpay Standard Checkout</span> is live in test mode. <b>Pay now</b> opens the embedded modal and verifies the signature server-side before marking the invoice paid. <b>Link</b> generates a shareable Razorpay Payment Link.
      </div>
    </AppLayout>
  );
}

function StatCard({ label, value, accent }) {
  return (
    <div className="miro-card p-5">
      <div className="miro-overline">{label}</div>
      <div className="font-display font-black text-2xl mt-2 text-[var(--miro-navy)]">{value}</div>
      <div className="mt-3 h-1 rounded-full" style={{ background: `${accent}30` }}>
        <div className="h-full rounded-full w-1/3" style={{ background: accent }}/>
      </div>
    </div>
  );
}
